import React, { useState } from 'react';
import {
	ITollSectionOffer,
	IOfferPrice,
	getFormattedTotalPrice,
	ILabel,
	ITypeVignetteOrderingProcessFields,
	formatTripsAmountLabel,
	tollSectionIsDisabled,
} from '@adac-mp/shared';
import { Button, Checkbox, Modal, TollSectionConfiguration, TollSectionInfo, Typography } from '../../index';

interface IRouteSelectionProps {
	route: ITollSectionOffer;
	displayOnMap?: boolean;
	correspondingTollSections?: ITollSectionOffer[];
	selectedVignetteTollSections?: ITollSectionOffer[];
	labels: ILabel[];
	contentfulLabels: ITypeVignetteOrderingProcessFields;
	addSelectedVignetteTollSections: (vignetteTollSection: ITollSectionOffer) => void;
	setHoveredTollSection?: (vignetteTollSection: ITollSectionOffer | undefined) => void;
	removeSelectedVignetteTollSectionsById: (id: number) => void;
}

export const renderTripsAmount = (prices: IOfferPrice[], trips?: number, labels?: ILabel[], disabled?: boolean) => {
	return (
		<>
			<span className="flex justify-between">
				<Typography color={disabled ? 'inactiveText' : 'title'} variant="title" size="lg">
					{formatTripsAmountLabel(trips, labels)}
				</Typography>
				<Typography color={disabled ? 'inactiveText' : 'title'} variant="title" size="lg">
					{getFormattedTotalPrice(prices)}
				</Typography>
			</span>
		</>
	);
};

const renderCheckboxLabel = (
	route: ITollSectionOffer,
	labels: ILabel[] | undefined,
	selectedProduct?: ITollSectionOffer,
) => {
	const { serviceCatalogName, downtimes, routeDirection, moduleTypeId } = route;
	return (
		<span className="flex flex-col gap-1">
			<span className="flex justify-between gap-x-2 ">
				<Typography variant="title" color="title" size="lg">
					{serviceCatalogName}
				</Typography>
				{selectedProduct && (
					<Typography color="title" variant="title" size="lg">
						{getFormattedTotalPrice(selectedProduct?.prices)}
					</Typography>
				)}
			</span>
			<TollSectionInfo
				downtimes={downtimes}
				routeDirection={routeDirection}
				sectionName={selectedProduct?.sectionName}
				labels={labels}
				trips={selectedProduct?.trips}
				moduleTypeId={moduleTypeId}
			/>
		</span>
	);
};

export const RouteSelection = ({
	route,
	correspondingTollSections,
	selectedVignetteTollSections,
	displayOnMap = false,
	labels,
	contentfulLabels,
	addSelectedVignetteTollSections,
	removeSelectedVignetteTollSectionsById,
	setHoveredTollSection = () => {},
}: IRouteSelectionProps) => {
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [productCode, setProductCode] = useState('');

	const routeIsDisabled = correspondingTollSections?.every(({ downtimes }) => tollSectionIsDisabled(downtimes));

	const tollSectionIsSelected = selectedVignetteTollSections?.some((toll) =>
		route.serviceCatalogName.startsWith(toll.serviceCatalogName),
	);

	const selectedTollSection = selectedVignetteTollSections?.find((toll) =>
		route.serviceCatalogName.startsWith(toll.serviceCatalogName),
	);

	const onClick = () => {
		if (selectedTollSection) {
			removeSelectedVignetteTollSectionsById(selectedTollSection.serviceCatalogId);
			setProductCode('');
			return;
		}
		setModalIsOpen(true);
	};

	const onSubmit = (contentRef?: HTMLDivElement | null) => {
		if (!productCode) {
			setHasError(true);
			setTimeout(() => {
				contentRef?.scroll({ top: contentRef.scrollHeight, behavior: 'smooth' });
			}, 0);
			return;
		}
		const currSelectedProduct = correspondingTollSections?.find(
			({ serviceCatalogCode }) => serviceCatalogCode === productCode,
		);
		if (currSelectedProduct) {
			addSelectedVignetteTollSections(currSelectedProduct);
			setModalIsOpen(false);
		}
	};

	const onClose = () => {
		setProductCode('');
		setHasError(false);
		setModalIsOpen(false);
	};

	return (
		<>
			<div
				className="bg-white first:rounded-t last:rounded-b"
				onMouseEnter={() => {
					setHoveredTollSection(route);
				}}
				onMouseLeave={() => {
					setHoveredTollSection(undefined);
				}}>
				<Checkbox
					className={`px-4 py-3 ${
						displayOnMap ? 'shadow-outerShadow rounded' : 'min-h-[80px] md:min-h-[112px]'
					}`}
					labelClassName="w-full"
					checked={tollSectionIsSelected}
					data-cy={`checkbox: ${route.serviceCatalogName}`}
					title={route.serviceCatalogName}
					label={renderCheckboxLabel(route, labels, selectedTollSection)}
					setChecked={onClick}
				/>
			</div>
			<Modal
				withSubmissionButtons={!routeIsDisabled}
				isOpen={modalIsOpen}
				title={route.serviceCatalogName}
				onSubmit={(contentRef) => onSubmit(contentRef)}
				onClose={onClose}>
				<TollSectionConfiguration
					route={route}
					hasError={hasError}
					productCode={productCode}
					correspondingTollSections={correspondingTollSections}
					labels={labels}
					contentfulLabels={contentfulLabels}
					setHasError={setHasError}
					setProductCode={setProductCode}
				/>
				{routeIsDisabled && (
					<Button fullWidth className="mt-3" variant="primary" onClick={() => setModalIsOpen(false)}>
						Abbrechen
					</Button>
				)}
			</Modal>
		</>
	);
};
